"use client";

import Image from "next/image";
import { signIn, type SignInResponse } from "next-auth/react";
import { useRouter, useSearchParams } from "next/navigation";
import { useCallback, useEffect, useMemo, useSyncExternalStore, useState } from "react";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { LoadingScreen } from "@/components/shared/loading-screen";
import { useAuthStore } from "@/features/auth";
import { passwordSignInErrorMessage } from "@/features/auth/utils/password-signin-error";
import { REFERRAL_PROGRAM_PROMISE } from "@/features/rewards/config/referral-cycle";
import { useValidateReferral } from "@/features/rewards/hooks";
import { readPendingReferralCode, writePendingReferralCode } from "@/features/rewards/utils/referral-cookie";
import { useToast } from "@/hooks/use-toast";
import { disableDemoMode, enableDemoMode, resetDemoOnboardingSession, seedCompletedDemoOnboardingSession } from "@/lib/mock-data/utils";
import { queryClient } from "@/lib/react-query";
import { apiClient } from "@/services/api-client";
import { cn } from "@/lib/utils";
type AuthMode = "in" | "new";
interface WhopAuthCardProps {
  initialMode?: AuthMode;
  redirect?: string | null;
}
const DEMO_EMAIL = "demo@sigmavue.com";
const DEMO_PASSWORD = "demo-login";
const LOCAL_ADMIN_ALIAS_EMAIL = process.env.NEXT_PUBLIC_LOCAL_ADMIN_SHORTCUT_ALIAS || "admin@sigmavue.local";
const LOCAL_ADMIN_ALIAS_PASSWORD = "admin-login";
const LOCAL_DEMO_ONBOARDING_PATH = "/dashboard/welcome";
// Admin is a production-equivalent real session — it lands on the live desk,
// never the seeded demo onboarding walkthrough.
const REAL_DASHBOARD_PATH = "/dashboard";
// Whop sign-in lands on the chromeless decision hop (app/post-signin/route.ts):
// it resolves onboarding truth server-side and 307s to /dashboard or
// /dashboard/welcome, so an incomplete member never sees dashboard chrome
// paint before being routed into onboarding.
const POST_SIGNIN_DECISION_PATH = "/post-signin";
const DEFAULT_WHOP_APP_URL = "https://whop.com/sigmavue/sigma-vue-dashboard-JMKJP2JcyEOltx/app/";
// SIG-244: legal docs live on the marketing site (apps/website /legal/*),
// not in this app — the old in-app /docs href 404'd.
const MARKETING_SITE_URL = process.env.NEXT_PUBLIC_MARKETING_SITE_URL || "https://sigmavue.io";
interface ValidReferral {
  code: string;
  referrerName: string | null;
  benefit: string | null;
}
interface AttributedCheckoutResponse {
  checkout_url: string;
}

// The OAuth client id is public by construction (it ships in the authorize
// URL), so the browser may read it — and it is the one honest signal for
// whether "Continue with Whop" can actually complete. Mirrors the server-side
// resolution in features/auth/providers/whop.ts.
const WHOP_SSO_CONFIGURED = Boolean(process.env.NEXT_PUBLIC_WHOP_APP_ID);
function getWhopAppUrl(): string {
  return process.env.NEXT_PUBLIC_WHOP_APP_URL || process.env.NEXT_PUBLIC_WHOP_CHECKOUT_URL || DEFAULT_WHOP_APP_URL;
}
function isLocalhostEnv(): boolean {
  if (typeof window === "undefined") return false;
  const host = window.location.hostname;
  if (["localhost", "127.0.0.1", "0.0.0.0"].includes(host)) return true;
  // Dev-only: also treat private LAN IPs (192.168.x / 10.x / 172.16-31.x) as
  // local so the demo/admin shortcuts work when the dev server is reached over
  // the LAN (e.g. headless-browser UI validation, which can't use `localhost`).
  // Guarded by NODE_ENV so production (real domain, never a private IP) is unaffected.
  if (process.env.NODE_ENV === "production") return false;
  return /^(192\.168\.|10\.|172\.(1[6-9]|2\d|3[01])\.)/.test(host);
}
const emptySubscribe = () => () => {};
function isLocalAdminShortcutEnabled(): boolean {
  return process.env.NODE_ENV !== "production";
}

// Operator 2026-09-15: staging (Vercel preview of `dev`) gets "Continue as
// Admin" too, behind NEXT_PUBLIC_ADMIN_SHORTCUT_ENABLED (docs/FLAGS.md:
// staging-only). Both reads are build-time inlined, so server and browser
// agree (no hydration split); auth.ts holds the real gate on the same pair.
export function isPreviewAdminShortcutEnabled(): boolean {
  return process.env.NEXT_PUBLIC_ADMIN_SHORTCUT_ENABLED === "true" && process.env.NEXT_PUBLIC_VERCEL_ENV === "preview";
}

/** Keep only same-origin path redirects — never an absolute/external URL. */
function safeRedirectPath(value: string | null | undefined): string | null {
  if (!value) return null;
  if (!value.startsWith("/") || value.startsWith("//")) return null;
  return value;
}

/** Desk URL → chromeless hop so an incomplete member never paints (app) chrome. */
function viaOnboardingDecisionHop(path: string): string {
  if (path === "/dashboard" || path.startsWith("/dashboard?")) {
    const query = path.includes("?") ? path.slice(path.indexOf("?")) : "";
    return `/post-signin${query}`;
  }
  return path;
}
function passwordSignInFailure(result: SignInResponse | undefined): {
  error: string;
  code: string | null;
} | null {
  if (result?.error) {
    return {
      error: result.error,
      code: result.code ?? null
    };
  }
  if (!result?.url) return null;
  const url = new URL(result.url, window.location.origin);
  const error = url.searchParams.get("error");
  if (!error) return null;
  return {
    error,
    code: url.searchParams.get("code")
  };
}
export function WhopAuthCard({
  initialMode = "in",
  redirect = null
}: WhopAuthCardProps) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const {
    toast
  } = useToast();
  const {
    mutateAsync: validateReferral
  } = useValidateReferral();
  const setLastUsedEmail = useAuthStore(state => state.setLastUsedEmail);
  const setAuthEvent = useAuthStore(state_0 => state_0.setAuthEvent);
  const [mode, setMode] = useState<AuthMode>(initialMode);
  const [isLoading, setIsLoading] = useState(false);
  // SIG-103: the CTAs below render (and visually accept clicks) from the SSR
  // HTML before React finishes hydrating and attaches their onClick handlers —
  // a click in that window is silently swallowed (no feedback, no network
  // call). Gate every CTA on this flag so it stays disabled until the mount
  // effect proves hydration completed, then it's a normal working button.
  const [isHydrated, setIsHydrated] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState<string | null>(null);
  const [redirectPath, setRedirectPath] = useState<string>(LOCAL_DEMO_ONBOARDING_PATH);
  const [validReferral, setValidReferral] = useState<ValidReferral | null>(null);
  // Purchase→access chain: /purchase-success prefills the checkout email and
  // marks the hop with post_checkout=1 so the subscription poller runs on land.
  const [email, setEmail] = useState(() => searchParams.get("email")?.trim() ?? "");
  const [password, setPassword] = useState("");
  const [passwordConfirmation, setPasswordConfirmation] = useState("");
  const [formError, setFormError] = useState<string | null>(() => {
    const error = searchParams.get("error");
    return error ? passwordSignInErrorMessage(error, searchParams.get("code")) : null;
  });
  // Whop SSO is the member path. The password form is not a member affordance
  // any more — it stays reachable only for legacy/admin accounts that predate
  // SSO, and it opens automatically when Whop sign-in is not configured so an
  // unconfigured deployment degrades into the old flow instead of a dead end.
  const [showPasswordFallback, setShowPasswordFallback] = useState(!WHOP_SSO_CONFIGURED);
  const isPostCheckout = searchParams.get("post_checkout") === "1";
  const referralCode = useMemo(() => {
    const code = searchParams.get("ref_code") ?? searchParams.get("referral_code");
    return code?.trim() || null;
  }, [searchParams]);
  // Hydration-safe: server snapshot is always false, client snapshot reads the
  // real hostname — avoids the SSR/client mismatch that regenerated the tree.
  const showLocalDemo = useSyncExternalStore(emptySubscribe, isLocalhostEnv, () => false);
  const showLocalAdmin = showLocalDemo && isLocalAdminShortcutEnabled() || isPreviewAdminShortcutEnabled();

  // Effects only run after hydration completes, so this flips exactly once
  // handlers are guaranteed attached.
  useEffect(() => {
    setIsHydrated(true);
  }, []);
  useEffect(() => {
    if (!referralCode) {
      setValidReferral(null);
      return;
    }

    // PR-215 P1: persist the code IMMEDIATELY — the cookie must not depend on
    // validation resolving before the user clicks the Whop CTA. Validation
    // only powers the banner UI; the backend attribute endpoint re-validates
    // (fail-open) anyway, so an invalid cookie is harmless.
    writePendingReferralCode(referralCode);
    let isCancelled = false;
    validateReferral(referralCode).then(result => {
      if (isCancelled) return;
      if (!result.valid) {
        setValidReferral(null);
        return;
      }
      const validated = {
        code: referralCode,
        referrerName: result.referrer_name,
        benefit: result.benefit
      };
      setValidReferral(validated);
      writePendingReferralCode(validated.code);
    }).catch(() => {
      if (!isCancelled) setValidReferral(null);
    });
    return () => {
      isCancelled = true;
    };
  }, [referralCode, validateReferral]);
  const routeToDestination = useCallback(() => {
    // #281: router.replace is a soft client navigation that keeps the session —
    // and an agent's CDP target — alive across the post-login transition. The old
    // synchronous `window.location.pathname` check that followed could never be
    // true yet (router.replace is async, so the pathname hasn't updated on this
    // tick), so it forced a `window.open(_self)` HARD navigation on every call,
    // wedging agent browsers into an about:blank target. Trust router.replace.
    router.replace(redirectPath);
  }, [router, redirectPath]);

  /** Post-auth landing: explicit redirect prop wins; post_checkout survives the hop. */
  const destinationFor = useCallback((fallback: string) => {
    const base = viaOnboardingDecisionHop(safeRedirectPath(redirect) ?? fallback);
    if (!isPostCheckout) return base;
    return base.includes("?") ? `${base}&post_checkout=1` : `${base}?post_checkout=1`;
  }, [redirect, isPostCheckout]);
  async function buyOnWhop() {
    if (typeof window === "undefined") return;
    const pendingReferralCode = referralCode ?? validReferral?.code ?? readPendingReferralCode();
    if (!pendingReferralCode) {
      window.open(getWhopAppUrl(), "_self");
      return;
    }
    writePendingReferralCode(pendingReferralCode);
    try {
      const response = await apiClient.postV2<AttributedCheckoutResponse>("/whop/v3/checkout-configuration", {
        referral_code: pendingReferralCode
      });
      window.open(response.data.checkout_url, "_self");
    } catch {
      window.open(getWhopAppUrl(), "_self");
    }
  }

  /**
   * The member sign-in path: one click, Whop proves who they are, the backend
   * mints the session and derives entitlement from the Whop membership. No
   * username, no password, no verification step.
   *
   * `redirect: true` hands the browser to Whop's authorize page, so this
   * function does not return on success — hence no post-auth cache reset here;
   * the callback lands on `destination` with a fresh session.
   */
  async function handleWhopSignIn() {
    setFormError(null);
    if (!WHOP_SSO_CONFIGURED) {
      // Honest, not silent: say what is wrong and open the path that works.
      setFormError("Whop sign-in isn't configured on this deployment yet. Use a password below, or contact support.");
      setShowPasswordFallback(true);
      return;
    }
    setIsLoading(true);
    setAuthEvent("signingIn");
    try {
      disableDemoMode();
      apiClient.clearSessionCache();
      await signIn("whop", {
        callbackUrl: destinationFor(POST_SIGNIN_DECISION_PATH)
      });
    } catch {
      setFormError("Couldn't reach Whop sign-in. Try again in a moment.");
      setIsLoading(false);
      setAuthEvent("getSession");
    }
  }

  /** Shared post-auth session reset for REAL users (never seeded demo state). */
  function resetRealSessionCaches(usedEmail: string, destination: string) {
    disableDemoMode();
    apiClient.clearSessionCache();
    queryClient.removeQueries({
      queryKey: ["onboarding"]
    });
    queryClient.invalidateQueries();
    setLastUsedEmail(usedEmail);
    setRedirectPath(destination);
    router.prefetch(destination);
  }
  async function handleSignIn(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setFormError(null);
    if (!email.trim() || !password) {
      setFormError("Email and password are required.");
      return;
    }
    setIsLoading(true);
    setAuthEvent("signingIn");
    try {
      const result_0 = await signIn("credentials", {
        email: email.trim(),
        password,
        redirect: false
      });
      const failure = passwordSignInFailure(result_0);
      if (failure) {
        setFormError(passwordSignInErrorMessage(failure.error, failure.code));
        return;
      }
      const destination_0 = destinationFor(POST_SIGNIN_DECISION_PATH);
      resetRealSessionCaches(email.trim(), destination_0);
      setLoadingMessage("Loading your desk...");
    } catch {
      setFormError("Sign-in hit an unexpected error. Try again.");
    } finally {
      setIsLoading(false);
      setAuthEvent("getSession");
    }
  }
  async function handleRegister(event_0: React.FormEvent<HTMLFormElement>) {
    event_0.preventDefault();
    setFormError(null);
    if (!email.trim() || !password) {
      setFormError("Email and password are required.");
      return;
    }
    if (password.length < 8) {
      setFormError("Password must be at least 8 characters.");
      return;
    }
    if (password !== passwordConfirmation) {
      setFormError("Passwords do not match.");
      return;
    }
    setIsLoading(true);
    setAuthEvent("signingIn");
    try {
      const registerResponse = await fetch("/api/auth/register", {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          email: email.trim(),
          password,
          password_confirmation: passwordConfirmation,
          ...(referralCode || validReferral ? {
            referral_code: referralCode ?? validReferral?.code
          } : {})
        })
      });
      if (!registerResponse.ok) {
        const payload = (await registerResponse.json().catch(() => ({}))) as {
          message?: string;
        };
        setFormError(payload.message || "Registration failed. Try again.");
        return;
      }
      const result_1 = await signIn("credentials", {
        email: email.trim(),
        password,
        redirect: false
      });
      if (result_1?.error) {
        setMode("in");
        toast({
          title: "Account created",
          description: "Check your email to verify, then sign in to reach your desk."
        });
        return;
      }
      toast({
        title: "Account created",
        description: "We emailed you a verification link — verifying attaches your Whop purchase."
      });
      const destination_1 = destinationFor(LOCAL_DEMO_ONBOARDING_PATH);
      resetRealSessionCaches(email.trim(), destination_1);
      setLoadingMessage("Setting up your desk...");
    } catch {
      setFormError("Registration hit an unexpected error. Try again.");
    } finally {
      setIsLoading(false);
      setAuthEvent("getSession");
    }
  }
  async function handleDemoLogin() {
    setIsLoading(true);
    setAuthEvent("signingIn");
    try {
      const result_2 = await signIn("credentials", {
        email: DEMO_EMAIL,
        password: DEMO_PASSWORD,
        redirect: false
      });
      if (result_2?.error) {
        toast({
          variant: "destructive",
          title: "Demo login failed",
          description: "Could not start a demo session."
        });
        return;
      }
      enableDemoMode();
      resetDemoOnboardingSession();
      apiClient.clearSessionCache();
      queryClient.removeQueries({
        queryKey: ["onboarding"]
      });
      queryClient.invalidateQueries();
      setLastUsedEmail(DEMO_EMAIL);
      setRedirectPath(LOCAL_DEMO_ONBOARDING_PATH);
      router.prefetch(LOCAL_DEMO_ONBOARDING_PATH);
      setLoadingMessage("Preparing onboarding...");
    } catch {
      toast({
        variant: "destructive",
        title: "Demo login failed",
        description: "Unexpected error while starting demo session."
      });
    } finally {
      setIsLoading(false);
      setAuthEvent("getSession");
    }
  }
  async function handleAdminLogin() {
    setIsLoading(true);
    setAuthEvent("signingIn");
    try {
      const result_3 = await signIn("credentials", {
        email: LOCAL_ADMIN_ALIAS_EMAIL,
        password: LOCAL_ADMIN_ALIAS_PASSWORD,
        redirect: false
      });
      if (result_3?.error) {
        toast({
          variant: "destructive",
          title: "Admin login failed",
          description: "Local admin shortcut is unavailable."
        });
        return;
      }

      // Admin is the operator's own production-equivalent session: NO seeded
      // demo data. Clear the demo-mode flag so every pillar (dashboard, office,
      // journal, accounts) reads the real backend for user 21. Seed a COMPLETED
      // onboarding session (never reset): user 21 finished onboarding on the
      // backend long ago, and wiping the local mock-store re-trapped admin in
      // the welcome wizard whenever any stale demo state made a guard consult
      // the mock path (E4 walk, 2026-07-10).
      disableDemoMode();
      seedCompletedDemoOnboardingSession();
      apiClient.clearSessionCache();
      queryClient.removeQueries({
        queryKey: ["onboarding"]
      });
      queryClient.invalidateQueries();
      setLastUsedEmail(LOCAL_ADMIN_ALIAS_EMAIL);
      setRedirectPath(REAL_DASHBOARD_PATH);
      router.prefetch(REAL_DASHBOARD_PATH);
      setLoadingMessage("Loading your desk...");
    } catch {
      toast({
        variant: "destructive",
        title: "Admin login failed",
        description: "Unexpected error while starting the local admin session."
      });
    } finally {
      setIsLoading(false);
      setAuthEvent("getSession");
    }
  }
  const isReturning = mode === "in";
  // Purchase→access: a buyer landing here with post_checkout=1 has ALREADY
  // paid — their Whop checkout account IS their login. Sending them to the
  // buy CTA again is the "loop to Whop forever" bug (SIG-745). When SSO can
  // complete, the primary action for them is Continue with Whop; the backend
  // derives their entitlement from the Whop membership at session mint.
  const postCheckoutBuyer = !isReturning && isPostCheckout && WHOP_SSO_CONFIGURED;
  const primaryIsWhopSignIn = isReturning || postCheckoutBuyer;
  const body = isReturning ? "You already have a Whop account — that's your login. One click and your desk opens; your seat comes across with you." : postCheckoutBuyer ? "Payment received. The Whop account you just checked out with is your login — continue with Whop and your seat attaches automatically." : "Buy your seat on Whop, then come straight back and continue with Whop. There's no second account to create.";
  if (loadingMessage) {
    return <LoadingScreen message={loadingMessage} durationMs={800} onComplete={routeToDestination} />;
  }
  const inputClassName = "h-12 w-full rounded-[13px] border border-white/10 bg-white/[0.03] px-4 text-[14px] text-white placeholder:text-white/30 outline-none transition focus:border-[#00ffcc]/50 focus:bg-white/[0.05]";
  return <div className="auth-card auth-card-glass relative w-full rounded-[26px] border border-white/[0.06] bg-[linear-gradient(155deg,rgba(23,30,43,.78),rgba(7,10,16,.92))] px-8 pb-7 pt-8 shadow-[inset_0_1px_0_rgba(255,255,255,.09),0_34px_100px_rgba(0,0,0,.48),0_0_52px_-24px_rgba(0,255,204,.28)] backdrop-blur-2xl" data-sentry-component="WhopAuthCard" data-sentry-source-file="whop-auth-card.tsx">
      <div role="tablist" aria-label="Sign in or create your account" className="auth-stagger auth-stagger-1 mb-7 grid grid-cols-2 gap-1 rounded-[14px] border border-white/10 bg-white/[0.02] p-1">
        <button type="button" role="tab" aria-selected={mode === "in"} className={cn("h-[42px] rounded-[10px] text-[13px] font-semibold transition-colors", mode === "in" ? "bg-[#00ffcc]/10 text-white shadow-[inset_0_0_0_1px_rgba(0,255,204,.28)]" : "text-white/42 hover:text-white/70")} onClick={() => {
        setFormError(null);
        setMode("in");
      }}>
          Continue with Whop
        </button>
        <button type="button" role="tab" aria-selected={mode === "new"} className={cn("h-[42px] rounded-[10px] text-[13px] font-semibold transition-colors", mode === "new" ? "bg-[#00ffcc]/10 text-white shadow-[inset_0_0_0_1px_rgba(0,255,204,.28)]" : "text-white/42 hover:text-white/70")} onClick={() => {
        setFormError(null);
        setMode("new");
      }}>
          Start with Whop
        </button>
      </div>

      <div role="tabpanel" className="auth-stagger auth-stagger-2">
        <h1 className="font-goldman text-[30px] font-bold leading-[1.05] tracking-normal text-white sm:text-[34px]">
          {isReturning ? <>
              Sign <em className="not-italic text-[#00ffcc]">in.</em>
            </> : <>
              Start with <em className="not-italic text-[#00ffcc]">Whop.</em>
            </>}
        </h1>
        <p className="text-white/58 mt-3 text-[13.5px] leading-6">{body}</p>

        {/* THE member path. A buyer already has a Whop account, so this is the
            only credential they need; entitlement is derived from their Whop
            membership by the backend at session mint. */}
        <button type="button" onClick={primaryIsWhopSignIn ? handleWhopSignIn : buyOnWhop} disabled={isLoading || !isHydrated} aria-busy={isLoading || !isHydrated} aria-label={primaryIsWhopSignIn ? "Continue with Whop member access" : "Start with Whop checkout + account — Buy a seat on Whop"} className="auth-whop-button mt-6 flex min-h-14 w-full items-center justify-center gap-2.5 rounded-[15px] border border-[#00ffcc]/30 bg-[linear-gradient(135deg,rgba(0,255,204,.15),rgba(0,255,204,.055))] px-4 text-[14.5px] font-semibold text-white transition hover:-translate-y-px hover:border-[#00ffcc] hover:bg-[#00ffcc]/10 disabled:translate-y-0 disabled:cursor-not-allowed disabled:opacity-60">
          {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <>
              <Image src="/logos/whop/whop-mark.svg" alt="" width={31} height={16} className="h-4 w-auto" />
              {primaryIsWhopSignIn ? "Continue with Whop" : "Get your seat on Whop"}
            </>}
        </button>

        <p className="mt-3 text-center text-[12px] leading-5 text-white/40">
          {primaryIsWhopSignIn ? "No password to remember. Your Whop membership is your access." : "You'll come back here and continue with Whop — no second account."}
        </p>

        {formError && !showPasswordFallback && <p role="alert" className="mt-3 text-[12.5px] leading-5 text-red-400/90">
            {formError}
          </p>}

        {isReturning && !showPasswordFallback && <button type="button" onClick={() => {
        setFormError(null);
        setShowPasswordFallback(true);
      }} className="mt-4 block w-full text-center text-[11.5px] leading-5 text-white/35 underline-offset-4 transition-colors hover:text-white/60 hover:underline">
            Use a password instead (legacy accounts)
          </button>}

        {showPasswordFallback && (isReturning || !WHOP_SSO_CONFIGURED) && <form className="mt-6 space-y-3" onSubmit={isReturning ? handleSignIn : handleRegister} aria-label={isReturning ? "Sign in form" : "Create account form"}>
            <label className="block">
              <span className="sr-only">Email</span>
              <input type="email" name="email" autoComplete="email" placeholder="you@example.com" value={email} onChange={e => setEmail(e.target.value)} className={inputClassName} required />
            </label>
            <label className="block">
              <span className="sr-only">Password</span>
              <input type="password" name="password" autoComplete={isReturning ? "current-password" : "new-password"} placeholder={isReturning ? "Password" : "Password (8+ characters)"} value={password} onChange={e_0 => setPassword(e_0.target.value)} className={inputClassName} required />
            </label>

            {!isReturning && <label className="block">
                <span className="sr-only">Confirm password</span>
                <input type="password" name="passwordConfirmation" autoComplete="new-password" placeholder="Confirm password" value={passwordConfirmation} onChange={e_1 => setPasswordConfirmation(e_1.target.value)} className={inputClassName} required />
              </label>}

            {formError && <p role="alert" className="text-[12.5px] leading-5 text-red-400/90">
                {formError}
              </p>}

            <button type="submit" disabled={isLoading || !isHydrated} aria-busy={isLoading || !isHydrated} className="auth-whop-button flex min-h-14 w-full items-center justify-center gap-2.5 rounded-[15px] border border-[#00ffcc]/30 bg-[linear-gradient(135deg,rgba(0,255,204,.15),rgba(0,255,204,.055))] px-4 text-[14.5px] font-semibold text-white transition hover:-translate-y-px hover:border-[#00ffcc] hover:bg-[#00ffcc]/10 disabled:translate-y-0 disabled:cursor-not-allowed disabled:opacity-60">
              {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <>{isReturning ? "Sign in to your desk" : "Create account"}</>}
            </button>
          </form>}

        {/* Secondary in sign-in mode only: a visitor who has no seat yet.
            In "Start with Whop" mode the primary CTA above already goes to
            checkout, so this would be the same button twice. */}
        {isReturning && <button type="button" onClick={buyOnWhop} disabled={isLoading || !isHydrated} aria-label="Buy a seat on Whop checkout" className="mt-4 flex min-h-12 w-full items-center justify-center gap-2 rounded-[13px] border border-white/10 bg-white/[0.03] px-4 text-[13px] font-semibold text-white/75 transition hover:border-white/25 hover:text-white disabled:cursor-not-allowed disabled:opacity-60">
            <Image src="/logos/whop/whop-mark.svg" alt="" width={31} height={16} className="h-3.5 w-auto opacity-90" />
            No seat yet? Buy on Whop
            <small className="font-mono text-[9px] uppercase tracking-[0.1em] text-white/40">
              checkout
            </small>
          </button>}

        {validReferral && <div className="glass glass-lit mt-4 rounded-[14px] border border-[#00ffcc]/20 bg-[#00ffcc]/[0.045] px-4 py-3">
            <div className="text-[12px] font-semibold text-white">
              Referred by {validReferral.referrerName || "a SigmaVue member"}
            </div>
            <div className="mt-1 text-[11px] leading-5 text-white/50">
              {validReferral.benefit || REFERRAL_PROGRAM_PROMISE}
            </div>
          </div>}

        <p className="text-white/38 mt-5 text-center text-[12px] leading-6">
          {isReturning ? <>
              New here?{" "}
              <button type="button" className="text-white/65 transition-colors hover:text-white" onClick={() => {
            setFormError(null);
            setMode("new");
          }}>
                Start with Whop →
              </button>
            </> : <>
              Already registered?{" "}
              <button type="button" className="text-white/65 transition-colors hover:text-white" onClick={() => {
            setFormError(null);
            setMode("in");
          }}>
                Sign in →
              </button>
            </>}
        </p>
      </div>

      {(showLocalDemo || showLocalAdmin) && <div className="auth-stagger auth-stagger-3 my-5 space-y-2 border-y border-white/10 py-4">
          {showLocalDemo && <Button type="button" variant="outline" className="w-full bg-white/[0.04]" disabled={isLoading || !isHydrated} aria-busy={isLoading || !isHydrated} onClick={handleDemoLogin}>
              Continue as Demo
            </Button>}
          {showLocalAdmin && <Button type="button" variant="outline" className="w-full bg-white/[0.04]" disabled={isLoading || !isHydrated} aria-busy={isLoading || !isHydrated} onClick={handleAdminLogin}>
              Continue as Admin
            </Button>}
          <p className="text-white/38 text-center text-[11px]">
            {showLocalDemo ? "Localhost only · Demo = seeded showcase · Admin = real data" : "Staging preview · Admin = real data"}
          </p>
        </div>}

      <p className="auth-stagger auth-stagger-4 text-white/38 mt-5 text-center text-[11px] leading-5">
        By continuing you agree to the{" "}
        <a href={`${MARKETING_SITE_URL}/legal/terms`} target="_blank" rel="noopener noreferrer" className="text-white/60 underline transition-colors hover:text-white">
          Terms
        </a>{" "}
        and{" "}
        <a href={`${MARKETING_SITE_URL}/legal/privacy`} target="_blank" rel="noopener noreferrer" className="text-white/60 underline transition-colors hover:text-white">
          Privacy Policy
        </a>
        .
      </p>
      <style>{`
        @keyframes authUp {
          to {
            opacity: 1;
            transform: none;
          }
        }

        .auth-stagger {
          opacity: 0;
          transform: translateY(14px);
          animation: authUp 0.6s cubic-bezier(0.16, 1, 0.3, 1) forwards;
        }

        .auth-stagger-1 {
          animation-delay: 0.04s;
        }

        .auth-stagger-2 {
          animation-delay: 0.1s;
        }

        .auth-stagger-3 {
          animation-delay: 0.16s;
        }

        .auth-stagger-4 {
          animation-delay: 0.22s;
        }

        @media (prefers-reduced-motion: reduce) {
          .auth-stagger {
            animation: none;
            opacity: 1;
            transform: none;
          }

          .auth-whop-button:hover {
            transform: none;
          }
        }
      `}</style>
    </div>;
}